// src/pages/admin/GeriEmbryos.tsx
import React, { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, ImageIcon, Play, Video } from "lucide-react";
import Header from "../../components/layouts/header";
import ApiService from "../../services/api";

interface FateRecord {
  _id: string;
  review_record: {
    assess1_score: number;
    assess2_algorithm: string;
    assess2_score: number | null;
    assess2_scores: Record<string, any>;
    code: string;
    comments: string;
    eeva_category: number;
    session_uuid: string;
    well_number: number;
  };
  well_number: number;
  fate_status: string;
  dish_uuid: string;
  session_uuid: string;
  account_id: string;
  machine_id: string;
  created_at: string;
  updated_at: string;
}

const GeriEmbryos: React.FC = () => {
  const { session_uuid } = useParams<{ session_uuid: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const patientName = location.state?.patientName || "";
  const identifier1 = location.state?.identifier1 || "";
  const dishUuid = location.state?.dishUuid || "";

  const [fateRecords, setFateRecords] = useState<FateRecord[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const [videoUrl, setVideoUrl] = useState("");
  const [loadingVideo, setLoadingVideo] = useState(false);
  const pageSize = 20;

  useEffect(() => {
    fetchFateRecords();
  }, [page, session_uuid]);

  const fetchFateRecords = async () => {
    setIsLoading(true);
    try {
      const data = await ApiService.fetchGeriFateRecords({
        session_uuid: session_uuid!,
        page,
        pageSize,
      });
      setFateRecords(data?.data || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error("Failed to fetch fate records:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchVideoUrl = async (session_uuid: string, well_number: number) => {
    setIsVideoOpen(true);
    setLoadingVideo(true);
    setVideoUrl("");

    try {
      const data = await ApiService.fetchGeriVideoUrl(
        session_uuid,
        well_number
      );
      setVideoUrl(data?.video_url || "");
    } catch (error) {
      console.error("Failed to fetch video URL:", error);
    } finally {
      setLoadingVideo(false);
    }
  };

  const totalPages = Math.ceil(total / pageSize);

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "empty":
        return "bg-gray-100 text-gray-800";
      case "none":
        return "bg-yellow-100 text-yellow-800";
      case "transferred":
        return "bg-green-100 text-green-800";
      case "frozen":
        return "bg-blue-100 text-blue-800";
      case "discarded":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Back Button & Info */}
        <div className="mb-6">
          <button
            onClick={() => navigate("/records")}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dish Records
          </button>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
              Embryo Wells (Fate Records)
            </h2>
            <div className="text-sm text-gray-600 space-y-1">
              <p>
                <span className="font-medium">Patient Name:</span>{" "}
                <span className="font-semibold text-gray-900">
                  {patientName}
                </span>
              </p>
              <p>
                <span className="font-medium">Identifier 1:</span>{" "}
                <span className="font-semibold text-gray-900">
                  {identifier1}
                </span>
              </p>
              <p>
                <span className="font-medium">Dish UUID:</span>{" "}
                <span className="font-semibold text-gray-900 font-mono text-xs">
                  {dishUuid}
                </span>
              </p>
              <p>
                <span className="font-medium">Session UUID:</span>{" "}
                <span className="font-semibold text-gray-900 font-mono text-xs">
                  {session_uuid}
                </span>
              </p>
            </div>
          </div>
        </div>

        {/* Fate Records List */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Wells / Embryos ({total})
            </h3>
          </div>

          {isLoading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-gray-600">Loading embryo wells...</p>
            </div>
          ) : fateRecords.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
              <p className="text-gray-500">No embryo wells recorded.</p>
            </div>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="hidden md:block bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Well Number
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Fate Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Assess1 Score
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Assess2 Score
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        EEVA Category
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Code
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Comments
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {fateRecords.map((record) => (
                      <tr
                        key={record._id}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-bold text-gray-900">
                            Well {record.well_number}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                              record.fate_status
                            )}`}
                          >
                            {record.fate_status || "N/A"}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {record.review_record.assess1_score || 0}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {record.review_record.assess2_score ?? "-"}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {record.review_record.eeva_category || 0}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {record.review_record.code || "-"}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900 max-w-xs truncate">
                            {record.review_record.comments || "-"}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {new Date(record.created_at).toLocaleString()}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() =>
                                navigate(
                                  `/geri/embryo-images/${record.session_uuid}/${record.well_number}`,
                                  {
                                    state: { fate_status: record.fate_status },
                                  }
                                )
                              }
                              className="inline-flex items-center px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors text-xs font-medium"
                              title="View Images"
                            >
                              <ImageIcon className="h-4 w-4 mr-1" />
                              Images
                            </button>
                            <button
                              onClick={() =>
                                fetchVideoUrl(
                                  record.session_uuid,
                                  record.well_number
                                )
                              }
                              className="inline-flex items-center px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-md transition-colors text-xs font-medium"
                              title="Play Video"
                            >
                              <Play className="h-4 w-4 mr-1" />
                              Video
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="md:hidden space-y-4">
                {fateRecords.map((record) => (
                  <div
                    key={record._id}
                    className="bg-white rounded-lg shadow-sm border border-gray-200 p-4"
                  >
                    <div className="space-y-3">
                      <div className="flex items-center justify-between pb-3 border-b border-gray-200">
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide">
                            Well Number
                          </p>
                          <p className="text-lg font-bold text-gray-900 mt-1">
                            Well {record.well_number}
                          </p>
                        </div>
                        <span
                          className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                            record.fate_status
                          )}`}
                        >
                          {record.fate_status || "N/A"}
                        </span>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide">
                            Assess1 Score
                          </p>
                          <p className="text-sm text-gray-900 mt-1">
                            {record.review_record.assess1_score || 0}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide">
                            Assess2 Score
                          </p>
                          <p className="text-sm text-gray-900 mt-1">
                            {record.review_record.assess2_score ?? "-"}
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide">
                            EEVA Category
                          </p>
                          <p className="text-sm text-gray-900 mt-1">
                            {record.review_record.eeva_category || 0}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide">
                            Code
                          </p>
                          <p className="text-sm text-gray-900 mt-1">
                            {record.review_record.code || "-"}
                          </p>
                        </div>
                      </div>

                      {record.review_record.comments && (
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide">
                            Comments
                          </p>
                          <p className="text-sm text-gray-900 mt-1">
                            {record.review_record.comments}
                          </p>
                        </div>
                      )}

                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">
                          Created
                        </p>
                        <p className="text-sm text-gray-900 mt-1">
                          {new Date(record.created_at).toLocaleString()}
                        </p>
                      </div>

                      <div className="flex space-x-2 pt-2">
                        <button
                          onClick={() =>
                            navigate(
                              `/geri/embryo-images/${record.session_uuid}/${record.well_number}`,
                              {
                                state: { fate_status: record.fate_status },
                              }
                            )
                          }
                          className="flex-1 inline-flex items-center justify-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md transition-colors"
                        >
                          <ImageIcon className="h-4 w-4 mr-2" />
                          View Images
                        </button>
                        <button
                          onClick={() =>
                            fetchVideoUrl(
                              record.session_uuid,
                              record.well_number
                            )
                          }
                          className="flex-1 inline-flex items-center justify-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-md transition-colors"
                        >
                          <Play className="h-4 w-4 mr-2" />
                          Play Video
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4 bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                  <div className="text-sm text-gray-700 font-medium">
                    Page: <span className="font-bold">{page}</span> /{" "}
                    <span className="font-bold">{totalPages}</span> | Total:{" "}
                    <span className="font-bold">{total}</span> records
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="px-4 py-2 text-sm font-medium text-white bg-black rounded-full hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      ◀ Previous
                    </button>
                    <button
                      onClick={() =>
                        setPage((p) => Math.min(totalPages, p + 1))
                      }
                      disabled={page === totalPages}
                      className="px-4 py-2 text-sm font-medium text-white bg-black rounded-full hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Next ▶
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Video Modal */}
      {isVideoOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full relative">
            <button
              onClick={() => {
                setIsVideoOpen(false);
                setVideoUrl("");
              }}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 bg-white rounded-full p-2 shadow-lg z-10"
            >
              <svg
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            <div className="p-6">
              {loadingVideo ? (
                <div className="text-center py-12">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <p className="mt-2 text-gray-600">Loading video...</p>
                </div>
              ) : videoUrl ? (
                <video
                  controls
                  autoPlay
                  className="w-full rounded-lg"
                  style={{ maxHeight: "70vh" }}
                >
                  <source src={videoUrl} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              ) : (
                <div className="text-center py-12">
                  <Video className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">Video not available.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GeriEmbryos;
